// src/pages/AbsenceRequests.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../../navigation/AuthProvider";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import { Toast } from "../../components/ToastPopup";
import Nav from "../../components/nav";
import AbsenceRequestModal from "../../components/AbsenceRequestModal";

import { getPermission } from "../../permissions/rolePermissions";


const PAGE_SIZE = 50;

// Column & action sizing (som i OvertimeRequests)
const CHECKBOX_COL = 40;       // px
const ACTION_TOTAL_WIDTH = 96; // två ikonknappar eller en restore
const ACTION_HEIGHT = 32;
const ACTION_GAP = 8;
const ICON_BTN_WIDTH = (ACTION_TOTAL_WIDTH - ACTION_GAP) / 2;

// Applicant | Scope | When | Type | Status | Reason/Feedback | Actions
const GRID_TEMPLATE = `${CHECKBOX_COL}px 1.4fr 1.2fr 1.6fr 1fr 1.2fr 1.8fr ${ACTION_TOTAL_WIDTH}px`;

const prettyDate = (d) => {
  try {
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toISOString().slice(0, 10);
  } catch {
    return d || "—";
  }
};
const fmtTime = (t) => (t || "").padStart(5, "0"); // HH:mm safe pad

// === Nytt: "Inskickat" formatterare i dot-format ===
const two = (n) => String(n).padStart(2, "0");
const formatSubmitted = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const ymd = `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
    const hm = `${two(d.getHours())}:${two(d.getMinutes())}`;
    return `${ymd} • ${hm}`;
  } catch {
    return "—";
  }
};

const AbsenceRequests = () => {
  const { user, company, timeSchemas, projects, draftSchedule, setDraftSchedule, absenceRules } = useAuth();

  const employeesPermission = getPermission(user.role, "cases:absence");
  const canEditAbsenceErrand = employeesPermission === "edit";
  
  // UI
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all"); // "pending" | "approved" | "rejected" | "all"

  // Data
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);

  // Selection
  const [selectedRequests, setSelectedRequests] = useState(new Set());

  // Modals
  const [activeRequest, setActiveRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reject prompt (single)
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Approve confirm (single)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveTargetId, setApproveTargetId] = useState(null);

  // Restore confirm (single)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreTargetId, setRestoreTargetId] = useState(null);

  // Bulk decision prompt
  const [bulkMode, setBulkMode] = useState(null); // "approve" | "reject" | null
  const [bulkNote, setBulkNote] = useState("");

  const monthsInSwedish = [
    "Januari","Februari","Mars","April","Maj","Juni",
    "Juli","Augusti","September","Oktober","November","December"
  ];

  // ---------- Styles ----------
  const styles = {
    wrap: { width: "100%", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
    container: { margin: "0 auto", padding: "24px 32px", maxWidth: 1600 },

    header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 24 },
    headerContent: { flex: 1 },
    pageTitle: { margin: 0, fontSize: 32, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.02em" },
    pageSubtitle: { margin: "4px 0 0", fontSize: 16, color: "#64748b", fontWeight: 400 },

    statsContainer: { display: "flex", gap: 16, flexWrap: "wrap" },
    statCard: { background: "white", padding: "16px 20px", borderRadius: 12, border: "1px solid #e2e8f0", minWidth: 140, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    statValue: { fontSize: 24, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 },
    statLabel: { fontSize: 12, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },

    controlsSection: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" },
    dateSelector: { display: "flex", alignItems: "center", gap: 12, background: "white", border: "1px solid #d1d5db", padding: "8px 16px", borderRadius: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },
    select: { background: "white", border: "1px solid #d1d5db", color: "#374151", padding: "6px 8px", borderRadius: 6, outline: "none", fontSize: 14, fontWeight: 500, minWidth: 120 },

    bulkBarShell: { height: 56, marginBottom: 16 },
    bulkActionsHidden: {
      visibility: "hidden",
      opacity: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid transparent",
      background: "transparent",
      pointerEvents: "none",
    },
    bulkActions: {
      visibility: "visible",
      opacity: 1,
      height: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      background: "#fff",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    bulkText: { fontSize: 13, fontWeight: 600, color: "#0f172a" },
    bulkSpacer: { flex: 1 },
    bulkBtn: {
      borderRadius: 6, border: "1px solid #d1d5db",
      background: "white", color: "#374151",
      cursor: "pointer", fontSize: 12, fontWeight: 700,
      height: ACTION_HEIGHT, minWidth: ACTION_TOTAL_WIDTH,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    },

    filterBar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "16px 20px", background: "white", borderRadius: 8, border: "1px solid #e5e7eb", marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    input: { flex: 1, minWidth: 280, background: "white", border: "1px solid #d1d5db", color: "#374151", padding: "8px 12px", borderRadius: 6, outline: "none", fontSize: 14, transition: "all 0.2s ease" },

    tableContainer: { background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", marginBottom: 24 },
    tableHeader: {
      display: "grid", gridTemplateColumns: GRID_TEMPLATE, gap: 16,
      padding: "14px 20px", color: "#374151", fontSize: 11, fontWeight: 800, background: "#f8fafc",
      borderBottom: "1px solid #e5e7eb", textTransform: "uppercase", letterSpacing: "0.06em",
      alignItems: "start"
    },
    thText: { lineHeight: 1, paddingTop: 2 },

    tableRow: {
      display: "grid", gridTemplateColumns: GRID_TEMPLATE, gap: 16,
      alignItems: "center",
      padding: "12px 20px", borderBottom: "1px solid #f3f4f6",
      cursor: "pointer", transition: "background-color 0.12s ease"
    },
    emptyState: { padding: "48px 20px", color: "#6b7280", fontSize: 14, textAlign: "center" },

    cell: { minWidth: 0 },

    checkbox: {
      width: 16, height: 16, borderRadius: 4, border: "2px solid #d1d5db",
      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "center"
    },
    checkboxSelected: { background: "#2563eb", borderColor: "#2563eb", color: "white" },
    checkboxDisabled: { opacity: 0.35, cursor: "not-allowed" },

    badge: (status) => {
      const base = { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, lineHeight: 1, border: "1px solid transparent" };
      if (status === "approved") return { ...base, background: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" };
      if (status === "rejected") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
      return { ...base, background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" };
    },

    btnBase: {
      borderRadius: 6, border: "1px solid transparent",
      background: "white", color: "#374151",
      cursor: "pointer", fontSize: 14, fontWeight: 700,
      height: ACTION_HEIGHT, display: "inline-flex",
      alignItems: "center", justifyContent: "center",
    },
    iconBtn: { width: ICON_BTN_WIDTH, padding: 0 },
    approveBtn: { background: "#16a34a", borderColor: "#16a34a", color: "white" },
    rejectBtn:  { background: "#ef4444", borderColor: "#ef4444", color: "white" },
    restoreBtn: { minWidth: ACTION_TOTAL_WIDTH, border: "1px solid #d1d5db", color: "#374151", background: "white", fontSize: 12 },

    pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
    paginationInfo: { fontSize: 14, color: "#6b7280", fontWeight: 500 },
    paginationControls: { display: "flex", alignItems: "center", gap: ACTION_GAP },
    // ✅ DESIGN FIX: centrera texten i pagination-knapparna exakt som på OvertimeRequests
    pageBtn: {
      borderRadius: 6,
      border: "1px solid #d1d5db",
      background: "white",
      color: "#374151",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      height: ACTION_HEIGHT,
      minWidth: ACTION_TOTAL_WIDTH,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
      padding: "0 12px",
      textAlign: "center",
      whiteSpace: "nowrap",
    },

    // ---- Simple prompts ----
    promptBackdrop: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 2147483647, padding: 20
    },
    promptBox: {
      width: "100%", maxWidth: 520, background: "#fff", borderRadius: 12,
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", padding: 18, border: "1px solid #e5e7eb"
    },
    promptTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" },
    promptP: { marginTop: 6, fontSize: 13, color: "#6b7280" },
    promptTextarea: {
      width: "100%", marginTop: 10, borderRadius: 8, border: "1px solid #d1d5db",
      padding: "10px 12px", fontSize: 14, outline: "none", resize: "vertical", minHeight: 90
    },
    promptActions: { display: "flex", gap: 10, marginTop: 14, justifyContent: "flex-end" },
    promptBtnGhost: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#111827", fontWeight: 700, cursor: "pointer"
    },
    promptBtnDanger: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #ef4444", background: "#ef4444", color: "#fff", fontWeight: 700, cursor: "pointer"
    },
    promptBtnPrimary: {
      padding: "8px 14px", borderRadius: 8, border: "1px solid #2563eb", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer"
    },

    // === Återanvändbara undertexter med ellipsis (redan på plats) ===
    shiftProject: { display: "block", fontWeight: 700, color: "#0f172a", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    shiftTime: { display: "block", fontSize: 13, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  };

  // ---------- Draft merge helpers ----------
  const upsertShiftInDraft = (updatedShift) => {
    if (!updatedShift || !updatedShift._id || typeof setDraftSchedule !== "function") return;
    setDraftSchedule((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const id = String(updatedShift._id);
      const idx = prevArr.findIndex((s) => String(s._id) === id);
      if (idx === -1) return [...prevArr, updatedShift];
      const copy = [...prevArr];
      copy[idx] = { ...copy[idx], ...updatedShift };
      return copy;
    });
  };

  const mergeManyShiftsInDraft = (shiftsArr = []) => {
    const valid = shiftsArr.filter((s) => s && s._id);
    if (valid.length === 0 || typeof setDraftSchedule !== "function") return;
    setDraftSchedule((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const byId = new Map(prevArr.map((s) => [String(s._id), s]));
      for (const incoming of valid) {
        const id = String(incoming._id);
        const existing = byId.get(id);
        byId.set(id, existing ? { ...existing, ...incoming } : incoming);
      }
      return Array.from(byId.values());
    });
  };

  // ---------- Data fetching ----------
  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/absence-requests`, {
        params: {
          userId: user._id,
          companyId: company._id,
          month: selectedMonth,
          year: selectedYear,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      });
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (err) {
      console.error("Kunde inte hämta frånvaroansökningar:", err);
      setToastType("error");
      setToastMessage(err?.response?.data?.message || "Kunde inte hämta frånvaroansökningar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRequests(); }, [company._id, user._id, selectedMonth, selectedYear, statusFilter]); // eslint-disable-line



  // Lägg strax ovanför filteredRequests
  const getTimeSchemaIdForReq = (r) => {
    if (r?.scope !== "shift") return null;
    const shiftId = (r.shiftId && (r.shiftId._id || r.shiftId.id)) || r.shiftId || r.shift;
    if (!shiftId) return null;
    const shift = (draftSchedule || []).find((s) => String(s._id) === String(shiftId));
    const ts = shift?.timeSchema || shift?.timeSchemaId;
    return ts ? String(ts) : null;
  };

  const schemaFiltered = useMemo(() => {
    if (!selectedSchema) return requests;
    const wanted = String(selectedSchema);
    return requests.filter((r) => {
      const ts = getTimeSchemaIdForReq(r);
      // om vi inte kan avgöra schema för en request, låt den passera (samma strategi som i övertidssidan)
      return ts ? ts === wanted : true;
    });
  }, [requests, selectedSchema, draftSchedule]);

  // ---------- Derived data ----------
  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = schemaFiltered; // <- använd schemat-filtret ovan
    if (!q) return base;

    return base.filter((r) => {
      const who       = ((r.employeeName || "") + " " + (r.employeeEmail || "")).toLowerCase();
      const status    = (r.status || "").toLowerCase();
      const dateStr   = ((r.shiftDate || r.dateFrom || "") + " " + (r.dateTo || "")).toLowerCase();
      const scope     = (r.scope || "").toLowerCase();
      const reason    = (r.reason || "").toLowerCase();
      const decision  = (r.decisionNote || "").toLowerCase();

      // Typnamn från regler
      const rule      = (absenceRules || []).find((rr) => String(rr._id) === String(r.type));
      const typeText  = (rule?.name || "").toLowerCase();

      // 1) ev. projekt från API
      const projectFromReq = (r.projectName || "").toLowerCase();

      // 2) projektnamn säkert härlett från draftSchedule + projects (för scope: "shift")
      const projectFromShift = (() => {
        try {
          const shiftId = (r.shiftId && (r.shiftId._id || r.shiftId.id)) || r.shiftId || r.shift;
          if (!shiftId) return "";
          const shift = (draftSchedule || []).find((s) => String(s._id) === String(shiftId));
          if (!shift) return "";
          const projId = shift.project || shift.projectId;
          const proj = (projects || []).find((p) => String(p._id) === String(projId));
          return (proj?.name || proj?.title || "").toLowerCase();
        } catch {
          return "";
        }
      })();

      return (
        who.includes(q) ||
        status.includes(q) ||
        dateStr.includes(q) ||
        scope.includes(q) ||
        typeText.includes(q) ||
        reason.includes(q) ||
        decision.includes(q) ||
        projectFromReq.includes(q) ||
        projectFromShift.includes(q)
      );
    });
  }, [schemaFiltered, searchQuery, absenceRules, draftSchedule, projects]);


  const stats = useMemo(() => {
    const total = filteredRequests.length;
    const pending = filteredRequests.filter(r => r.status === 'pending').length;
    const approved = filteredRequests.filter(r => r.status === 'approved').length;
    const rejected = filteredRequests.filter(r => r.status === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [filteredRequests]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRequests.slice(start, start + PAGE_SIZE);
  }, [filteredRequests, page]);

  useEffect(() => {
    setPage(1);
    setSelectedRequests(new Set());
  }, [searchQuery, selectedSchema, selectedMonth, selectedYear, statusFilter]);

  // ---------- Selection & Actions ----------
  const isPending = (r) => r.status === "pending";

  const toggleSelectRequest = (requestId) => {
    const target = requests.find(x => x._id === requestId);
    if (target && !isPending(target)) return; // får bara välja väntande
    const next = new Set(selectedRequests);
    if (next.has(requestId)) next.delete(requestId);
    else next.add(requestId);
    setSelectedRequests(next);
  };

  const everyIdSelected = (ids) => ids.length && ids.every(id => selectedRequests.has(id));

  const toggleSelectAll = () => {
    const onlyPendingIds = paged.filter(isPending).map(r => r._id);
    if (onlyPendingIds.length && everyIdSelected(onlyPendingIds)) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(onlyPendingIds));
    }
  };

  // Single decide helper
  const decideRequest = async (reqId, action, decisionNote) => {
    const payload = {
      userId: user._id,
      companyId: company._id,
      requestId: reqId,
      action,            // "approve" | "reject" | "restore"
      decisionNote,      // backend fält
    };
    const { data } = await axios.post(`${BACKEND_URL}/absence-requests/decide`, payload);
    if (data?.shift) upsertShiftInDraft(data.shift);
    if (Array.isArray(data?.shifts) && data.shifts.length) mergeManyShiftsInDraft(data.shifts);
    return data;
  };

  // Bulk via iterering
  const runBulk = async (action, decisionNote) => {
    if (selectedRequests.size === 0) return;
    setLoading(true);
    try {
      const ids = Array.from(selectedRequests);
      const collected = [];
      for (const id of ids) {
        const res = await decideRequest(id, action, decisionNote);
        if (res?.shift) collected.push(res.shift);
        if (Array.isArray(res?.shifts)) collected.push(...res.shifts);
      }
      if (collected.length) mergeManyShiftsInDraft(collected);

      setToastType("success");
      setToastMessage(
        action === "approve"
          ? `${ids.length} ansökningar godkända.`
          : `${ids.length} ansökningar nekade.`
      );
      setSelectedRequests(new Set());
      await loadRequests();
    } catch (err) {
      console.error("Bulk decide failed:", err);
      setToastType("error");
      setToastMessage("Kunde inte uppdatera alla valda ansökningar.");
    } finally {
      setLoading(false);
    }
  };

  // ----- Bulk prompt handlers -----
  const openBulkApprove = () => {
    if (selectedRequests.size) {
      setBulkMode("approve");
      setBulkNote("");
    }
  };
  const openBulkReject = () => {
    if (selectedRequests.size) {
      setBulkMode("reject");
      setBulkNote("");
    }
  };
  const closeBulkPrompt = () => {
    setBulkMode(null);
    setBulkNote("");
  };
  const confirmBulk = async () => {
    if (!bulkMode) return;
    if (bulkMode === "approve") {
      await runBulk("approve");
    } else {
      await runBulk("reject", (bulkNote || "").trim() || undefined);
    }
    closeBulkPrompt();
  };

  // ----- Reject (single) -----
  const openRejectPrompt = (reqId) => {
    setRejectTargetId(reqId);
    setRejectReason("");
    setShowRejectPrompt(true);
  };
  const closeRejectPrompt = () => {
    setShowRejectPrompt(false);
    setRejectTargetId(null);
    setRejectReason("");
  };
  const confirmReject = async () => {
    if (!rejectTargetId) return;
    try {
      setLoading(true);
      await decideRequest(rejectTargetId, "reject", rejectReason.trim() || undefined);
      setToastType("success");
      setToastMessage("Ansökan nekad.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setIsModalOpen(false)
      setLoading(false);
      closeRejectPrompt();
    }
  };

  // ----- Approve (single) with confirm -----
  const requestApprove = (reqId) => { setApproveTargetId(reqId); setShowApproveConfirm(true); };
  const closeApproveConfirm = () => { setShowApproveConfirm(false); setApproveTargetId(null); };
  const confirmApprove = async () => {
    if (!approveTargetId) return;
    try {
      setLoading(true);
      await decideRequest(approveTargetId, "approve");
      setToastType("success");
      setToastMessage("Ansökan godkänd.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setIsModalOpen(false)
      setLoading(false);
      closeApproveConfirm();
    }
  };

  // ----- Restore (single) -----
  const openRestoreConfirm = (reqId) => { setRestoreTargetId(reqId); setShowRestoreConfirm(true); };
  const closeRestoreConfirm = () => { setShowRestoreConfirm(false); setRestoreTargetId(null); };
  const confirmRestore = async () => {
    if (!restoreTargetId) return;
    try {
      setLoading(true);
      await decideRequest(restoreTargetId, "restore");
      setToastType("success");
      setToastMessage("Ansökan återställd.");
      await loadRequests();
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera ansökan.");
    } finally {
      setIsModalOpen(false)
      setLoading(false);
      closeRestoreConfirm();
    }
  };

  // ----- Helpers — projekt & affected shifts -----
  const getProjectNameForShift = useCallback((shiftIdOrObj) => {
    try {
      const shiftId = typeof shiftIdOrObj === "object" ? (shiftIdOrObj?._id || shiftIdOrObj?.id) : shiftIdOrObj;
      const shift = (draftSchedule || []).find((s) => String(s._id) === String(shiftId));
      if (!shift) return "—";
      const projId = shift.project || shift.projectId;
      const proj = (projects || []).find((p) => String(p._id) === String(projId));
      return proj?.name || proj?.title || "—";
    } catch {
      return "—";
    }
  }, [draftSchedule, projects]);

  const shiftHasEmployee = (shift, employeeId) =>
    Array.isArray(shift?.employees) &&
    shift.employees.some((e) => String(e.employeeId) === String(employeeId));

  const findAffectedShiftsForRequest = useCallback((req) => {
    if (!req || req.scope !== "period") return [];
    const eid = req.employee || req.employeeId;
    if (!eid || !draftSchedule?.length) return [];
    const from = req.dateFrom;
    const to = req.dateTo;
    return (draftSchedule || [])
      .filter((s) => s.date && s.date >= from && s.date <= to && shiftHasEmployee(s, eid))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [draftSchedule]);

  const ruleName = (r) => {
    const rule = (absenceRules || []).find((rr) => String(rr._id) === String(r.type));
    return rule?.name || "—";
  };

  // ====== Cells ======

  // Cell: När (pass/period)
  const WhenCell = ({ r }) => {
    if (r.scope === "shift") {
      const datePart = r.shiftDate || "—";
      const original =
        r.appliesWholeShift
          ? (r.shiftStart && r.shiftEnd ? `${fmtTime(r.shiftStart)}–${fmtTime(r.shiftEnd)}` : "—")
          : (r.fromTime && r.toTime ? `${fmtTime(r.fromTime)}–${fmtTime(r.toTime)}` : "—");
      return (
        <span style={{ ...styles.cell, lineHeight: 1.25 }}>
          <strong style={styles.shiftProject}>
            {getProjectNameForShift((r.shiftId && r.shiftId._id) || r.shiftId)}
          </strong>
          <span style={styles.shiftTime}>
            {datePart} • {original}
          </span>
        </span>
      );
    }
    // scope === "period"
    const from = prettyDate(r.dateFrom);
    const to = prettyDate(r.dateTo);
    return (
      <span style={{ ...styles.cell, lineHeight: 1.25 }}>
        <strong style={styles.shiftProject}>Period</strong>
        <span style={styles.shiftTime}>
          {from}{to && from !== to ? ` → ${to}` : ""}
        </span>
      </span>
    );
  };

  // Cell: Typ
  const TypeCell = ({ r }) => {
    const title = ruleName(r);
    if (r.scope === "shift") {
      const sub = r.appliesWholeShift
        ? "Gäller: Hela passet"
        : `Gäller: ${fmtTime(r.fromTime)}–${fmtTime(r.toTime)}`;
      return (
        <span style={{ ...styles.cell, lineHeight: 1.25 }}>
          <span style={{ display: "block", fontSize: 13, color: "#374151", fontWeight: 600, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {title}
          </span>
          {/* ⬇️ Enda ändringen: display:'block' så ellipsis funkar identiskt som i andra subtexter */}
          <span style={{ display: "block", fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {sub}
          </span>
        </span>
      );
    }
    return (
      <span style={{ ...styles.cell, display: "block", fontSize: 13, color: "#374151", fontWeight: 600, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {title}
      </span>
    );
  };

  // Modal helpers
  const openModal = (req) => { setActiveRequest(req); setIsModalOpen(true); };
  const closeModal = () => { setActiveRequest(null); setIsModalOpen(false); };

  return (
    <div style={styles.wrap}>
      <Nav/>
      <div style={styles.container}>
        {/* Header with Stats */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.pageTitle}>Frånvaroansökningar</h1>
            <p style={styles.pageSubtitle}>Administrera och granska frånvaro per pass eller period</p>

            <div style={styles.statsContainer}>
              <div style={styles.statCard}><div style={styles.statValue}>{stats.total}</div><div style={styles.statLabel}>Totalt</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#92400e"}}>{stats.pending}</div><div style={styles.statLabel}>Väntar</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#166534"}}>{stats.approved}</div><div style={styles.statLabel}>Godkända</div></div>
              <div style={styles.statCard}><div style={{...styles.statValue, color: "#991b1b"}}>{stats.rejected}</div><div style={styles.statLabel}>Nekade</div></div>
            </div>
          </div>
        </div>

        {/* Controls (Month / Year) */}
        <div style={styles.controlsSection}>
          <div style={styles.dateSelector}>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(+e.target.value)} style={styles.select}>
              {monthsInSwedish.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(+e.target.value)} style={styles.select}>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div />
        </div>

        {/* Bulk bar */}
        <div style={styles.bulkBarShell}>
          <div style={selectedRequests.size > 0 ? styles.bulkActions : styles.bulkActionsHidden}>
            <span style={styles.bulkText}>
              {selectedRequests.size} valda
            </span>
            <div style={styles.bulkSpacer} />
            <button onClick={openBulkApprove} style={styles.bulkBtn}>Godkänn</button>
            <button onClick={openBulkReject}  style={styles.bulkBtn}>Neka</button>
            <button onClick={() => setSelectedRequests(new Set())} style={styles.bulkBtn}>Rensa</button>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filterBar}>
          <input
            style={styles.input}
            type="text"
            placeholder="Sök namn, email, status, datum, projekt, typ, skäl eller återkoppling…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select value={selectedSchema} onChange={(e) => setSelectedSchema(e.target.value)} style={styles.select}>
            <option value="">Alla scheman</option>
            {timeSchemas.map((s) => (<option key={s._id} value={s._id}>{s.name}</option>))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
            <option value="all">Alla status</option>
            <option value="pending">Väntar</option>
            <option value="approved">Godkända</option>
            <option value="rejected">Nekade</option>
          </select>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
          <div
            style={{
              ...styles.checkbox,
              ...(!canEditAbsenceErrand ? styles.checkboxDisabled : {})
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!canEditAbsenceErrand) return;
              toggleSelectAll();
            }}
            title={
              canEditAbsenceErrand
                ? "Markera alla väntande på sidan"
                : "Du har endast läsbehörighet"
            }
          />

            <span style={styles.thText}>Sökande</span>
            <span style={styles.thText}>Omfattning</span>
            <span style={styles.thText}>När</span>
            <span style={styles.thText}>Typ</span>
            <span style={styles.thText}>Status</span>
            <span style={styles.thText}>Skäl / Återkoppling</span>
            {canEditAbsenceErrand && (<span style={styles.thText}>Åtgärd</span>)}
          </div>

          {loading ? (
            <div style={styles.emptyState}>Laddar frånvaroansökningar...</div>
          ) : paged.length === 0 ? (
            <div style={styles.emptyState}>
              {searchQuery || selectedSchema || statusFilter !== "all"
                ? "Inga ansökningar matchar dina sökkriterier."
                : "Inga frånvaroansökningar för valt datumintervall."}
            </div>
          ) : (
            paged.map((r) => {
              const pending = r.status === "pending";
              const reason = r.reason || "";               // skäl från anställd
              const feedback = r.decisionNote || "";       // återkoppling från admin
              return (
                <div
                  key={r._id}
                  style={styles.tableRow}
                  onClick={() => openModal(r)}
                >
                  {/* Checkbox */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canEditAbsenceErrand) return;
                      if (pending) toggleSelectRequest(r._id);
                    }}
                    style={{
                      ...styles.checkbox,
                      ...(!canEditAbsenceErrand || !pending
                        ? styles.checkboxDisabled
                        : selectedRequests.has(r._id)
                          ? styles.checkboxSelected
                          : {}),
                    }}
                    title={
                      !canEditAbsenceErrand
                        ? "Du har endast läsbehörighet"
                        : pending
                          ? selectedRequests.has(r._id)
                            ? "Avmarkera"
                            : "Markera"
                          : "Redan beslutad"
                    }
                  >
                    {canEditAbsenceErrand && pending && selectedRequests.has(r._id) ? "✓" : ""}
                  </div>


                  {/* Sökande + Inskickat */}
                  <span style={{ ...styles.cell, lineHeight: 1.25 }}>
                    <strong style={styles.shiftProject}>
                      {r.employeeName || r.employeeEmail || "—"}
                    </strong>
                    <span style={styles.shiftTime}>
                      Inskickad {formatSubmitted(r.createdAt)}
                    </span>
                  </span>

                  {/* Omfattning */}
                  <span style={{ ...styles.cell, fontSize: 12, fontWeight: 700, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.scope === "shift" ? "Pass" : r.scope === "period" ? "Period" : "—"}
                  </span>

                  {/* När */}
                  <WhenCell r={r} />

                  {/* Typ */}
                  <TypeCell r={r} />

                  {/* Status */}
                  <span style={styles.cell}>
                    <span style={styles.badge(r.status)}>
                      {r.status === "approved" ? "Godkänd" : r.status === "rejected" ? "Nekad" : "Väntar"}
                    </span>
                  </span>

                  {/* Skäl / Återkoppling */}
                  <span style={{ ...styles.cell, fontSize: 13, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {reason && feedback
                      ? `Skäl: ${reason} • Återkoppling: ${feedback}`
                      : reason
                      ? `Skäl: ${reason}`
                      : feedback
                      ? `Återkoppling: ${feedback}`
                      : "—"}
                  </span>

                  {/* Åtgärd */}
                  <div
                    style={{ display: "flex", gap: ACTION_GAP, justifyContent: "flex-end", alignItems: "center", width: ACTION_TOTAL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canEditAbsenceErrand && (
                      <>
                        {pending ? (
                        <>
                          <button
                            onClick={() => requestApprove(r._id)}
                            style={{ ...styles.btnBase, ...styles.iconBtn, ...styles.approveBtn }}
                            title="Godkänn"
                            aria-label="Godkänn"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => openRejectPrompt(r._id)}
                            style={{ ...styles.btnBase, ...styles.iconBtn, ...styles.rejectBtn }}
                            title="Neka"
                            aria-label="Neka"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => openRestoreConfirm(r._id)}
                          style={{ ...styles.btnBase, ...styles.restoreBtn }}
                          title="Återställ till väntande"
                        >
                          Återställ
                        </button>
                       )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Visar {Math.min(PAGE_SIZE, paged.length)} av {filteredRequests.length} ansökningar
            </div>
            <div style={styles.paginationControls}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}
              >
                Föregående
              </button>
              <span style={{ fontSize: 14, color: "#6b7280", padding: "0 16px" }}>
                Sida {page} av {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}
              >
                Nästa
              </button>
            </div>
          </div>
        )}

        {/* Absence modal */}
        <AbsenceRequestModal
          open={isModalOpen && !!activeRequest}
          request={activeRequest}
          onClose={() => { setActiveRequest(null); setIsModalOpen(false); }}
          onApprove={() => { if (activeRequest) { setApproveTargetId(activeRequest._id); setShowApproveConfirm(true); } }}
          onReject={() => { if (activeRequest) { setRejectTargetId(activeRequest._id); setShowRejectPrompt(true); } }}
          ruleName={ruleName}
          getProjectNameForShift={getProjectNameForShift}
          findAffectedShiftsForRequest={findAffectedShiftsForRequest}
          isReadOnly={!canEditAbsenceErrand}
        />

        {/* Reject prompt (single) */}
        {showRejectPrompt && (
          <div style={styles.promptBackdrop} onClick={closeRejectPrompt}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Anledning till nekning</h3>
              <p style={styles.promptP}>Valfritt: skriv en kort kommentar som sparas som återkoppling till den anställde.</p>
              <textarea
                style={styles.promptTextarea}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Skriv återkoppling (valfritt)…"
              />
              <div style={styles.promptActions}>
                <button onClick={closeRejectPrompt} style={styles.promptBtnGhost}>Avbryt</button>
                <button onClick={confirmReject} style={styles.promptBtnDanger}>Bekräfta nekning</button>
              </div>
            </div>
          </div>
        )}

        {/* Approve confirm (single) */}
        {showApproveConfirm && (
          <div style={styles.promptBackdrop} onClick={closeApproveConfirm}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Bekräfta godkännande</h3>
              <p style={styles.promptP}>Vill du godkänna denna ansökan?</p>
              <div style={styles.promptActions}>
                <button onClick={closeApproveConfirm} style={styles.promptBtnGhost}>Avbryt</button>
                <button onClick={confirmApprove} style={styles.promptBtnPrimary}>Godkänn</button>
              </div>
            </div>
          </div>
        )}

        {/* Restore confirm (single) */}
        {showRestoreConfirm && (
          <div style={styles.promptBackdrop} onClick={closeRestoreConfirm}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>Bekräfta återställning</h3>
              <p style={styles.promptP}>Vill du återställa denna ansökan till väntande?</p>
              <div style={styles.promptActions}>
                <button onClick={closeRestoreConfirm} style={styles.promptBtnGhost}>Avbryt</button>
                <button onClick={confirmRestore} style={styles.promptBtnPrimary}>Återställ</button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk decision prompt */}
        {bulkMode && (
          <div style={styles.promptBackdrop} onClick={closeBulkPrompt}>
            <div style={styles.promptBox} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.promptTitle}>
                {bulkMode === "approve" ? "Bulk-godkänning" : "Bekräfta bulk-nekning"}
              </h3>

              {bulkMode !== "approve" && (
                <>
                  <p style={styles.promptP}>
                    Du är på väg att neka {selectedRequests.size} st ansökningar. Valfritt: ange en gemensam återkoppling som sparas på samtliga.
                  </p>
                  <textarea
                    style={styles.promptTextarea}
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                    placeholder="Skriv gemensam återkoppling (valfritt)…"
                  />
                </>
              )}

              <div style={styles.promptActions}>
                <button onClick={closeBulkPrompt} style={styles.promptBtnGhost}>Avbryt</button>
                {bulkMode === "approve" ? (
                  <button onClick={confirmBulk} style={styles.promptBtnPrimary}>Bekräfta</button>
                ) : (
                  <button onClick={confirmBulk} style={styles.promptBtnDanger}>Bekräfta nekning</button>
                )}
              </div>
            </div>
          </div>
        )}

        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage("")} />
      </div>
    </div>
  );
};

export default AbsenceRequests;
